import { useState, useRef, useEffect, useCallback, forwardRef } from 'react';
import { simulateTitles, ARCHETYPES } from './ctrSimulator';
import SimulatorResultsView from './SimulatorResultsView';

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4000').replace(/\/$/, '');
const API_TOKEN = process.env.REACT_APP_API_TOKEN || '';

const CTR_COLORS = ['#a78bfa', '#4ade80', '#f59e0b'];

// Simulation loading screen component
const SimLoadingScreen = forwardRef(({ titles, progress, currentTitle, simPhase, viewerCount, clickCount, activeArchetype, currentRound, totalRounds }, ref) => {
  const totalViewers = 100000 * titles.length;
  const processedViewers = viewerCount;
  const overallPct = Math.round((processedViewers / totalViewers) * 100);
  const roundProgress = totalRounds > 1
    ? ((currentRound - 1) / totalRounds + (overallPct / 100) / totalRounds) * 100
    : overallPct;

  const phases = [
    { label: 'Generating viewer profiles', icon: '1' },
    { label: 'Running click decisions', icon: '2' },
    { label: 'Computing confidence intervals', icon: '3' },
    { label: 'Finalizing results', icon: '4' },
  ];

  return (
    <div ref={ref} style={loadingStyles.overlay}>
      <div style={loadingStyles.card}>
        <div style={loadingStyles.header}>
          <div style={loadingStyles.pulseRing}>
            <div style={loadingStyles.pulseCore} />
          </div>
          <h2 style={loadingStyles.title}>Simulation Running</h2>
          <p style={loadingStyles.subtitle}>
            Testing {titles.length} title{titles.length > 1 ? 's' : ''} against {(totalViewers * totalRounds).toLocaleString()} synthetic viewers
          </p>
        </div>

        {/* Round counter */}
        {totalRounds > 1 && (
          <div style={loadingStyles.roundSection}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#e2e8f0' }}>
                Round {currentRound} of {totalRounds}
              </span>
              <span style={{ fontSize: '12px', color: '#a78bfa', fontFamily: 'monospace', fontWeight: '700' }}>
                {Math.round(roundProgress)}% overall
              </span>
            </div>
            <div style={{ display: 'flex', gap: '4px' }}>
              {Array.from({ length: totalRounds }, (_, i) => (
                <div key={i} style={{
                  flex: 1, height: '6px', borderRadius: '3px',
                  background: i < currentRound - 1 ? '#4ade80' : i === currentRound - 1
                    ? `linear-gradient(90deg, #a78bfa ${overallPct}%, rgba(255,255,255,0.08) ${overallPct}%)`
                    : 'rgba(255,255,255,0.08)',
                  transition: 'background 0.3s ease',
                }} />
              ))}
            </div>
          </div>
        )}

        {/* Phase steps */}
        <div style={loadingStyles.phases}>
          {phases.map((p, i) => {
            const isActive = i === simPhase;
            const isDone = i < simPhase;
            return (
              <div key={i} style={{ ...loadingStyles.phaseRow, opacity: isDone ? 0.5 : isActive ? 1 : 0.3 }}>
                <div style={{
                  ...loadingStyles.phaseIcon,
                  background: isDone ? '#4ade80' : isActive ? '#a78bfa' : 'rgba(255,255,255,0.1)',
                  color: isDone || isActive ? '#fff' : '#475569',
                }}>
                  {isDone ? (
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="#fff"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                  ) : p.icon}
                </div>
                <span style={{ ...loadingStyles.phaseLabel, color: isActive ? '#e2e8f0' : '#64748b' }}>
                  {p.label}
                  {isActive && <span style={loadingStyles.phaseDots}>...</span>}
                </span>
              </div>
            );
          })}
        </div>

        {/* Live viewer counter */}
        <div style={loadingStyles.counterSection}>
          <div style={loadingStyles.counterRow}>
            <span style={loadingStyles.counterLabel}>Viewers processed</span>
            <span style={loadingStyles.counterValue}>{processedViewers.toLocaleString()}</span>
          </div>
          <div style={loadingStyles.counterRow}>
            <span style={loadingStyles.counterLabel}>Clicks recorded</span>
            <span style={{ ...loadingStyles.counterValue, color: '#4ade80' }}>{clickCount.toLocaleString()}</span>
          </div>
          {activeArchetype && (
            <div style={loadingStyles.counterRow}>
              <span style={loadingStyles.counterLabel}>Current archetype</span>
              <span style={{ ...loadingStyles.counterValue, color: '#a78bfa', fontSize: '13px' }}>{activeArchetype}</span>
            </div>
          )}
        </div>

        {/* Overall progress bar */}
        <div style={loadingStyles.progressSection}>
          <div style={loadingStyles.progressTrack}>
            <div style={{
              ...loadingStyles.progressFill,
              width: `${overallPct}%`,
            }} />
          </div>
          <span style={loadingStyles.progressPct}>{overallPct}%</span>
        </div>

        {/* Per-title progress */}
        <div style={loadingStyles.titleProgress}>
          {titles.map((title, i) => {
            const pct = Math.round(progress[i] * 100);
            const isProcessing = i === currentTitle;
            return (
              <div key={i} style={loadingStyles.titleRow}>
                <span style={{
                  ...loadingStyles.titleDot,
                  background: pct >= 100 ? '#4ade80' : isProcessing ? CTR_COLORS[i] : 'rgba(255,255,255,0.15)',
                  boxShadow: isProcessing ? `0 0 8px ${CTR_COLORS[i]}66` : 'none',
                }} />
                <span style={{ ...loadingStyles.titleName, color: isProcessing ? '#e2e8f0' : '#64748b' }}>
                  {title.length > 40 ? title.slice(0, 40) + '...' : title}
                </span>
                <span style={{ ...loadingStyles.titlePct, color: pct >= 100 ? '#4ade80' : '#94a3b8' }}>
                  {pct >= 100 ? 'Done' : `${pct}%`}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});


const SimulatorPage = ({ user, updateUser, showCreditToast }) => {
  const hasCredits = (user?.requestLimit ?? 0) > 0;
  const canUseFeature = hasCredits;
  const deductCredit = (newLimit) => {
    if (updateUser && user) {
      updateUser({ requestLimit: typeof newLimit === 'number' ? newLimit : Math.max((user.requestLimit ?? 0) - 1, 0) });
    }
  };
  const [titles, setTitles] = useState(['', '', '']);
  const [results, setResults] = useState(null);
  const [running, setRunning] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [progress, setProgress] = useState([0, 0, 0]);
  const [currentTitle, setCurrentTitle] = useState(0);
  const [thumbnailStrength, setThumbnailStrength] = useState(5);
  const [simPhase, setSimPhase] = useState(0);
  const [viewerCount, setViewerCount] = useState(0);
  const [clickCount, setClickCount] = useState(0);
  const [activeArchetype, setActiveArchetype] = useState('');
  const [currentRound, setCurrentRound] = useState(1);
  const [allRuns, setAllRuns] = useState(null); // array of 10 run results
  const [aiAnalysis, setAiAnalysis] = useState(null); // Call 2 results
  const [aiLoading, setAiLoading] = useState(false);
  const totalRounds = 10;
  const abortRef = useRef(false);
  const pendingResultsRef = useRef(null);
  const loadingRef = useRef(null);

  const validTitles = titles.filter(t => t.trim().length > 0);
  const canRun = validTitles.length > 0 && !running;

  // Cycle through archetype names during simulation
  useEffect(() => {
    if (!showLoading) return;
    const names = ARCHETYPES.map(a => a.name);
    let idx = 0;
    const interval = setInterval(() => {
      idx = (idx + 1) % names.length;
      setActiveArchetype(names[idx]);
    }, 400);
    return () => clearInterval(interval);
  }, [showLoading]);

  // Advance simPhase based on overall progress
  useEffect(() => {
    if (!showLoading) return;
    const totalViewers = 100000 * validTitles.length;
    const pct = totalViewers > 0 ? viewerCount / totalViewers : 0;
    if (pct >= 0.9) setSimPhase(3);
    else if (pct >= 0.4) setSimPhase(2);
    else if (pct >= 0.05) setSimPhase(1);
    else setSimPhase(0);
  }, [viewerCount, showLoading, validTitles.length]);

  const revealResults = useCallback(() => {
    if (pendingResultsRef.current) {
      setResults(pendingResultsRef.current);
      pendingResultsRef.current = null;
    }
    setShowLoading(false);
    setRunning(false);
  }, []);

  // Fetch AI signal scores (Call 1) — runs in parallel with simulation, costs 1 credit
  const fetchAISignals = async (titleTexts) => {
    try {
      const res = await fetch(`${API_BASE_URL}/simulator-ai/signals`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${API_TOKEN}`,
          'X-User-Email': user?.email || '',
        },
        body: JSON.stringify({ titles: titleTexts }),
      });
      if (!res.ok) return null;
      const data = await res.json();
      // Deduct credit locally to keep UI in sync
      if (typeof data.creditsRemaining === 'number') {
        deductCredit(data.creditsRemaining);
        if (showCreditToast) showCreditToast(1);
      }
      return data;
    } catch { return null; }
  };

  // Fetch AI post-sim analysis (Call 2) — runs after simulation
  const fetchAIAnalysis = async (aggregatedResults, simulationData) => {
    try {
      const payload = aggregatedResults.map(r => ({
        title: r.title,
        ctr: r.ctr,
        archetypeBreakdown: r.archetypeBreakdown.map(a => ({ name: a.name, ctr: a.ctr })),
        signals: r.signals,
      }));
      const body = { results: payload };
      // Include full simulation data so the server can save to history
      if (simulationData) body.simulationData = simulationData;
      const res = await fetch(`${API_BASE_URL}/simulator-ai/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${API_TOKEN}`,
          'X-User-Email': user?.email || '',
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) return null;
      return await res.json();
    } catch { return null; }
  };

  const runSimulation = async () => {
    abortRef.current = false;
    setRunning(true);
    setResults(null);
    setAllRuns(null);
    setAiAnalysis(null);
    setAiLoading(false);
    setProgress([0, 0, 0]);
    setCurrentTitle(0);
    setSimPhase(0);
    setViewerCount(0);
    setClickCount(0);
    setCurrentRound(1);
    setActiveArchetype(ARCHETYPES[0].name);
    pendingResultsRef.current = null;

    // Show loading screen and scroll to it
    setShowLoading(true);
    setTimeout(() => {
      loadingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);

    const minDisplayMs = 6000 + Math.random() * 4000;
    const loadingStarted = Date.now();

    const toSimulate = titles.map((t, i) => ({ text: t.trim(), idx: i })).filter(t => t.text.length > 0);
    const thumbMult = 0.5 + (thumbnailStrength / 10) * 1.0;
    const simOpts = { thumbnailMult: thumbMult };
    const runsCollected = [];

    // Call 1: Fire AI signal scoring in parallel with simulation
    let aiSignalsPromise = fetchAISignals(toSimulate.map(t => t.text));

    try {
      for (let round = 1; round <= totalRounds; round++) {
        if (abortRef.current) break;
        setCurrentRound(round);
        setProgress([0, 0, 0]);
        setCurrentTitle(0);
        setSimPhase(0);

        // On round 1, try to merge AI signals if available
        let roundOpts = simOpts;
        if (round === 1 && aiSignalsPromise) {
          const aiScores = await aiSignalsPromise;
          aiSignalsPromise = null; // only await once
          if (aiScores?.titles) {
            // Pass AI scores as overrides — simulator will blend them with regex
            roundOpts = { ...simOpts, aiSignalOverrides: aiScores.titles };
          }
        }

        const simResults = await simulateTitles(
          toSimulate.map(t => t.text),
          100000,
          (titleIdx, processed, total) => {
            if (abortRef.current) return;
            setCurrentTitle(titleIdx);
            setProgress(prev => {
              const next = [...prev];
              next[toSimulate[titleIdx].idx] = processed / total;
              return next;
            });
            const viewersSoFar = titleIdx * 100000 + processed;
            setViewerCount(viewersSoFar);
            setClickCount(prev => {
              const estimated = Math.round(viewersSoFar * 0.04);
              return Math.max(prev, estimated);
            });
          },
          roundOpts
        );

        if (abortRef.current) break;
        runsCollected.push(simResults);
        // Reset counters for next round
        setViewerCount(100000 * toSimulate.length);
        setProgress([1, 1, 1]);
        setSimPhase(3);
      }

      if (!abortRef.current && runsCollected.length > 0) {
        // Aggregate results across all runs
        const numTitles = runsCollected[0].length;
        const aggregated = [];
        for (let t = 0; t < numTitles; t++) {
          const ctrs = runsCollected.map(run => run[t].ctr);
          const avgCtr = ctrs.reduce((s, c) => s + c, 0) / ctrs.length;
          const minCtr = Math.min(...ctrs);
          const maxCtr = Math.max(...ctrs);
          const stdDev = Math.sqrt(ctrs.reduce((s, c) => s + (c - avgCtr) ** 2, 0) / ctrs.length);
          const consistency = Math.max(0, 100 - (stdDev / (avgCtr || 0.001)) * 100);
          const totalClicks = runsCollected.reduce((s, run) => s + run[t].clicks, 0);
          const totalImpressions = runsCollected.reduce((s, run) => s + run[t].impressions, 0);

          aggregated.push({
            ...runsCollected[0][t], // base signals, title, archetypeBreakdown from first run
            ctr: avgCtr,
            clicks: Math.round(totalClicks / runsCollected.length),
            impressions: Math.round(totalImpressions / runsCollected.length),
            ctrLow: minCtr,
            ctrHigh: maxCtr,
            consistency: Math.round(consistency),
            allCtrs: ctrs,
            stdDev,
            // Average archetype breakdown across runs
            archetypeBreakdown: runsCollected[0][t].archetypeBreakdown.map((arch, ai) => ({
              ...arch,
              ctr: runsCollected.reduce((s, run) => s + run[t].archetypeBreakdown[ai].ctr, 0) / runsCollected.length,
            })),
          });
        }

        // Head-to-head wins (how many of 10 rounds each title won)
        if (numTitles > 1) {
          const wins = Array(numTitles).fill(0);
          for (const run of runsCollected) {
            let bestIdx = 0;
            for (let t = 1; t < numTitles; t++) {
              if (run[t].ctr > run[bestIdx].ctr) bestIdx = t;
            }
            wins[bestIdx]++;
          }
          aggregated.forEach((a, i) => { a.wins = wins[i]; });
        }

        pendingResultsRef.current = aggregated;
        setAllRuns(runsCollected);
        const totalClicks = aggregated.reduce((s, r) => s + r.clicks, 0);
        setClickCount(totalClicks);

        // Call 2: AI post-sim analysis — wait for it before revealing results
        // Server saves to history during this call so data persists even if user navigates away
        setAiLoading(true);
        const simulationData = user?.email ? {
          results: aggregated.map(r => ({
            title: r.title,
            ctr: r.ctr,
            ctrLow: r.ctrLow,
            ctrHigh: r.ctrHigh,
            clicks: r.clicks,
            impressions: r.impressions,
            consistency: r.consistency,
            wins: r.wins,
            allCtrs: r.allCtrs,
            signals: r.signals,
            archetypeBreakdown: r.archetypeBreakdown,
          })),
          thumbnailStrength,
          totalRounds,
        } : null;
        const analysisPromise = fetchAIAnalysis(aggregated, simulationData);

        const elapsed = Date.now() - loadingStarted;
        const remaining = Math.max(0, minDisplayMs - elapsed);

        // Wait for both minimum display time and AI analysis
        const [analysis] = await Promise.all([
          analysisPromise,
          remaining > 0 ? new Promise(r => setTimeout(r, remaining)) : Promise.resolve(),
        ]);
        setAiAnalysis(analysis);
        setAiLoading(false);

        revealResults();
      }
    } catch {
      setShowLoading(false);
      setRunning(false);
    }
  };

  const reset = () => {
    abortRef.current = true;
    setRunning(false);
    setShowLoading(false);
    setResults(null);
    setAllRuns(null);
    setAiAnalysis(null);
    setAiLoading(false);
    setCurrentRound(1);
    setProgress([0, 0, 0]);
    pendingResultsRef.current = null;
  };


  // Non-pro gate
  if (!canUseFeature) {
    return (
      <div style={pageStyles.page}>
        <div style={pageStyles.container}>
          <div style={pageStyles.gate}>
            <svg viewBox="0 0 24 24" width="40" height="40" fill="#a78bfa" style={{ marginBottom: '12px' }}>
              <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
            </svg>
            <h2 style={pageStyles.gateTitle}>CTR Simulator</h2>
            <p style={pageStyles.gateText}>
              Run 10 rounds of 100,000 viewer impressions to get reliable CTR estimates for your titles.
              Each simulation uses 1 credit.
            </p>
            <button onClick={() => { window.location.hash = '#/settings'; }} style={pageStyles.gateBtn}>
              {user ? 'Get More Credits' : 'Sign Up for Free Credits'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyles.page}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse-ring {
          0% { transform: scale(0.8); opacity: 0.5; }
          50% { transform: scale(1.2); opacity: 0.2; }
          100% { transform: scale(0.8); opacity: 0.5; }
        }
        @keyframes pulse-core {
          0% { transform: scale(1); }
          50% { transform: scale(1.15); }
          100% { transform: scale(1); }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes dots {
          0%, 20% { content: ''; }
          40% { content: '.'; }
          60% { content: '..'; }
          80%, 100% { content: '...'; }
        }
        @media (max-width: 768px) {
          .sim-cards-row { grid-template-columns: 1fr !important; }
          .res-sw-grid { grid-template-columns: 1fr !important; }
          .res-signal-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
      <div style={pageStyles.container}>
        {/* Header */}
        <div style={pageStyles.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h1 style={pageStyles.title}>CTR Simulator</h1>
            <span style={pageStyles.badge}>BETA</span>
          </div>
          <p style={pageStyles.subtitle}>
            Run 10 rounds of 100,000 synthetic viewers per title for statistically reliable CTR estimates.
            Enter up to 3 titles and compare them side by side.
          </p>
        </div>

        {/* How it works */}
        <div style={pageStyles.howItWorks}>
          <h3 style={pageStyles.howTitle}>How it works</h3>
          <div style={pageStyles.howGrid}>
            <div style={pageStyles.howStep}>
              <span style={pageStyles.howNum}>1</span>
              <div>
                <strong style={{ color: '#e2e8f0', fontSize: '13px' }}>Enter titles</strong>
                <p style={pageStyles.howText}>Add up to 3 title variations to compare</p>
              </div>
            </div>
            <div style={pageStyles.howStep}>
              <span style={pageStyles.howNum}>2</span>
              <div>
                <strong style={{ color: '#e2e8f0', fontSize: '13px' }}>Profile generation</strong>
                <p style={pageStyles.howText}>100K viewers per round across 8 archetypes with randomized mood, scroll position, session depth & competition</p>
              </div>
            </div>
            <div style={pageStyles.howStep}>
              <span style={pageStyles.howNum}>3</span>
              <div>
                <strong style={{ color: '#e2e8f0', fontSize: '13px' }}>Click decisions</strong>
                <p style={pageStyles.howText}>10 independent rounds averaged together — shows consistency, head-to-head wins, and reliable CTR estimates</p>
              </div>
            </div>
          </div>
        </div>

        {/* Title inputs */}
        <div style={pageStyles.inputSection}>
          <h3 style={pageStyles.sectionTitle}>Titles to Test</h3>
          <div style={simStyles.inputs}>
            {titles.map((title, i) => (
              <div key={i}>
                <div style={simStyles.inputRow}>
                  <span style={{ ...simStyles.inputNum, background: `${CTR_COLORS[i]}22`, color: CTR_COLORS[i] }}>
                    {i + 1}
                  </span>
                  <input
                    type="text"
                    value={title}
                    onChange={e => {
                      const next = [...titles];
                      next[i] = e.target.value;
                      setTitles(next);
                    }}
                    placeholder={i === 0 ? 'Enter your title...' : 'Optional comparison title...'}
                    style={simStyles.input}
                    disabled={running}
                  />
                  {title && !running && (
                    <button onClick={() => { const next = [...titles]; next[i] = ''; setTitles(next); }} style={simStyles.clearBtn}>
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="#64748b"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Settings row */}
          <div style={simStyles.settingsGrid}>
            {/* Thumbnail slider */}
            <div style={simStyles.sliderRow}>
              <label style={simStyles.sliderLabel}>
                Thumbnail Quality
                <span style={simStyles.sliderHint}>Great thumbnails amplify clicks</span>
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, maxWidth: '280px' }}>
                <span style={simStyles.sliderEndLabel}>Weak</span>
                <input
                  type="range" min="1" max="10" value={thumbnailStrength}
                  onChange={e => setThumbnailStrength(Number(e.target.value))}
                  style={simStyles.slider}
                  disabled={running}
                />
                <span style={simStyles.sliderEndLabel}>Strong</span>
                <span style={simStyles.sliderValue}>{thumbnailStrength}/10</span>
              </div>
            </div>



          </div>

          <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
            <button onClick={runSimulation} disabled={!canRun} style={{
              ...simStyles.runBtn,
              opacity: canRun ? 1 : 0.5,
              cursor: canRun ? 'pointer' : 'not-allowed',
            }}>
              {running ? `Round ${currentRound}/${totalRounds}...` : 'Run Simulation'}
            </button>
            {(running || results) && (
              <button onClick={reset} style={simStyles.resetBtn}>Reset</button>
            )}
          </div>
        </div>

        {/* Simulation loading screen */}
        {showLoading && (
          <SimLoadingScreen
            ref={loadingRef}
            titles={validTitles}
            progress={progress}
            currentTitle={currentTitle}
            simPhase={simPhase}
            viewerCount={viewerCount}
            clickCount={clickCount}
            activeArchetype={activeArchetype}
            currentRound={currentRound}
            totalRounds={totalRounds}
          />
        )}

        {/* Results */}
        {results && (
          <div style={{ marginTop: '32px' }}>
            <SimulatorResultsView
              results={results}
              aiAnalysis={aiAnalysis}
              totalRounds={totalRounds}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const pageStyles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(160deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: '#e2e8f0',
  },
  container: {
    maxWidth: '960px',
    margin: '0 auto',
    padding: '48px 24px 80px',
  },
  header: {
    marginBottom: '32px',
  },
  title: {
    fontSize: '28px', fontWeight: '800', margin: 0,
    letterSpacing: '-1px', color: '#e2e8f0',
  },
  subtitle: {
    fontSize: '14px', color: '#94a3b8', margin: '8px 0 0',
    lineHeight: '1.6', maxWidth: '600px',
  },
  badge: {
    fontSize: '9px', fontWeight: '700', color: '#fbbf24', letterSpacing: '0.5px',
    background: 'rgba(251,191,36,0.15)', padding: '3px 10px', borderRadius: '4px',
  },
  sectionTitle: {
    margin: '0 0 12px', color: '#e2e8f0', fontSize: '16px', fontWeight: '600',
  },
  howItWorks: {
    background: 'rgba(124,58,237,0.06)', border: '1px solid rgba(124,58,237,0.15)',
    borderRadius: '14px', padding: '20px 24px', marginBottom: '32px',
  },
  howTitle: {
    margin: '0 0 14px', fontSize: '14px', fontWeight: '600', color: '#c4b5fd',
  },
  howGrid: {
    display: 'flex', gap: '20px', flexWrap: 'wrap',
  },
  howStep: {
    display: 'flex', alignItems: 'flex-start', gap: '12px', flex: '1 1 200px',
  },
  howNum: {
    width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
    background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '13px', fontWeight: '700', color: '#fff',
  },
  howText: {
    margin: '4px 0 0', fontSize: '12px', color: '#94a3b8', lineHeight: '1.5',
  },
  inputSection: {
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '14px', padding: '24px',
  },
  // Pro gate
  gate: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', textAlign: 'center',
    padding: '80px 24px', minHeight: '60vh',
  },
  gateTitle: {
    fontSize: '24px', fontWeight: '700', color: '#e2e8f0', margin: '0 0 12px',
  },
  gateText: {
    fontSize: '14px', color: '#94a3b8', lineHeight: '1.6', maxWidth: '400px',
    margin: '0 0 24px',
  },
  gateBtn: {
    padding: '10px 28px', borderRadius: '10px', border: 'none',
    background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
    color: '#fff', fontSize: '14px', fontWeight: '600',
    cursor: 'pointer', fontFamily: 'inherit',
  },
};

const simStyles = {
  inputs: {
    display: 'flex', flexDirection: 'column', gap: '8px',
  },
  inputRow: {
    display: 'flex', alignItems: 'center', gap: '10px',
  },
  inputNum: {
    width: '24px', height: '24px', borderRadius: '6px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '12px', fontWeight: '700', flexShrink: 0,
  },
  input: {
    flex: 1, border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px',
    padding: '10px 14px', background: 'rgba(255,255,255,0.05)', color: '#e2e8f0',
    fontSize: '13px', fontFamily: 'inherit', outline: 'none',
  },
  clearBtn: {
    background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
    display: 'flex', alignItems: 'center', flexShrink: 0,
  },
  runBtn: {
    padding: '10px 28px', borderRadius: '10px', border: 'none',
    background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
    color: '#fff', fontSize: '14px', fontWeight: '600',
    fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  resetBtn: {
    padding: '10px 20px', borderRadius: '10px',
    border: '1px solid rgba(255,255,255,0.12)', background: 'transparent',
    color: '#94a3b8', fontSize: '13px', fontWeight: '500',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  settingsGrid: {
    display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px',
  },
  sliderRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: '16px', flexWrap: 'wrap',
  },
  sliderLabel: {
    fontSize: '13px', fontWeight: '600', color: '#e2e8f0',
    display: 'flex', flexDirection: 'column', gap: '2px',
  },
  sliderHint: {
    fontSize: '11px', fontWeight: '400', color: '#64748b',
  },
  slider: {
    flex: 1, height: '4px', appearance: 'none', WebkitAppearance: 'none',
    background: 'rgba(255,255,255,0.12)', borderRadius: '2px', outline: 'none',
    cursor: 'pointer', accentColor: '#a78bfa',
  },
  sliderEndLabel: {
    fontSize: '10px', color: '#64748b', flexShrink: 0,
  },
  sliderValue: {
    fontSize: '13px', fontWeight: '700', color: '#a78bfa',
    fontFamily: 'monospace', width: '32px', textAlign: 'center', flexShrink: 0,
  },
};

const loadingStyles = {
  overlay: {
    marginTop: '24px',
    animation: 'fade-in-up 0.4s ease',
  },
  card: {
    background: 'rgba(15,23,42,0.95)',
    border: '1px solid rgba(124,58,237,0.25)',
    borderRadius: '16px',
    padding: '32px',
    backdropFilter: 'blur(8px)',
  },
  header: {
    textAlign: 'center',
    marginBottom: '28px',
  },
  pulseRing: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    border: '2px solid rgba(167,139,250,0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
    animation: 'pulse-ring 2s ease-in-out infinite',
  },
  pulseCore: {
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #a78bfa, #6366f1)',
    animation: 'pulse-core 1.5s ease-in-out infinite',
  },
  title: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#e2e8f0',
    margin: '0 0 6px',
    letterSpacing: '-0.5px',
  },
  subtitle: {
    fontSize: '13px',
    color: '#94a3b8',
    margin: 0,
  },
  phases: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '24px',
    padding: '16px',
    background: 'rgba(255,255,255,0.02)',
    borderRadius: '10px',
    border: '1px solid rgba(255,255,255,0.05)',
  },
  phaseRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    transition: 'opacity 0.4s ease',
  },
  phaseIcon: {
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '10px',
    fontWeight: '700',
    flexShrink: 0,
    transition: 'background 0.4s ease',
  },
  phaseLabel: {
    fontSize: '12px',
    fontWeight: '500',
    transition: 'color 0.4s ease',
  },
  phaseDots: {
    color: '#a78bfa',
    fontWeight: '400',
  },
  counterSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '20px',
    padding: '14px 16px',
    background: 'rgba(124,58,237,0.06)',
    borderRadius: '10px',
    border: '1px solid rgba(124,58,237,0.12)',
  },
  counterRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  counterLabel: {
    fontSize: '12px',
    color: '#64748b',
  },
  counterValue: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#e2e8f0',
    fontFamily: 'monospace',
  },
  roundSection: {
    marginBottom: '20px',
    padding: '14px 16px',
    background: 'rgba(167,139,250,0.08)',
    borderRadius: '10px',
    border: '1px solid rgba(167,139,250,0.15)',
  },
  progressSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
  },
  progressTrack: {
    flex: 1,
    height: '8px',
    borderRadius: '4px',
    background: 'rgba(255,255,255,0.08)',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: '4px',
    background: 'linear-gradient(90deg, #7c3aed, #a78bfa, #7c3aed)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s linear infinite',
    transition: 'width 0.3s ease',
  },
  progressPct: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#a78bfa',
    fontFamily: 'monospace',
    width: '36px',
    textAlign: 'right',
  },
  titleProgress: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  titleDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    flexShrink: 0,
    transition: 'all 0.3s ease',
  },
  titleName: {
    fontSize: '12px',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    transition: 'color 0.3s ease',
  },
  titlePct: {
    fontSize: '12px',
    fontWeight: '600',
    fontFamily: 'monospace',
    width: '40px',
    textAlign: 'right',
    flexShrink: 0,
  },
};

export default SimulatorPage;
